import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { GenericTabsComponent, GenericTab, GenericTabConfig, ModalMode, HeaderService, CardComponent, DataListComponent } from '@solcre-org/core-ui';
import { Contact } from './components/contact/contact';
import { Quotes } from './components/quotes/quotes';
import { Booking } from './components/booking/booking';
import { Notes } from './components/notes/notes';
import { Rates } from './components/rates/rates';
import { ApiService } from '@solcre-org/core';
import { environment } from '../../../../../environments/environment.local';
import { ActivatedRoute } from '@angular/router';
import { ClientModel } from '../../../../shared/models/client.model';
import { getCountryByCode } from '../../../../shared/data/countries';

@Component({
  selector: 'client-view',
  imports: [GenericTabsComponent, TranslateModule, Contact, Quotes, Booking, Notes, Rates, CardComponent, DataListComponent],
  templateUrl: './client-view.html',
})
export class ClientView implements OnInit {

  private apiService: ApiService = inject(ApiService);
  private headerService: HeaderService = inject(HeaderService);
  private route = inject(ActivatedRoute);
  private hasUserInteracted = signal<boolean>(false);
  private activeTabId = signal<string>('contact');
  clientId = signal<string | null>(null);
  endpoint = computed<string>(() => {
    return environment.API.CLIENTS_VIEW_URI.replace(':id', this.clientId() || '');
  });
  fieldErrors = signal<Map<string, string[]>>(new Map());
  showValidationErrors = signal<boolean>(false);
  modalMode = signal<ModalMode>(ModalMode.EDIT);
  editedData = signal<any | null>(null);
  client = signal<ClientModel | null>(null);
  clientDataList = computed(() => {
    const clientData = this.client();
    if (!clientData) return [];

    return [
      { key: 'commercialName', label: 'clients.commercialName', value: clientData.commercialName || '-' },
      { key: 'legalName', label: 'clients.legalName', value: clientData.legalName || '-' },
      { key: 'fiscalId', label: 'clients.fiscalId', value: clientData.fiscalId || '-' },
      { key: 'country', label: 'clients.country', value: clientData.country ? getCountryByCode(clientData.country)?.label || clientData.country : '-' },
      { key: 'address', label: 'clients.address', value: clientData.address || '-' },
    ];
  });
  tabs = computed<GenericTab[]>(() => [
    {
      id: 'contact',
      translationKey: 'contact.title',
      active: this.activeTabId() === 'contact'
    },
    {
      id: 'quotes',
      translationKey: 'quotes.title',
      active: this.activeTabId() === 'quotes',
      disabled: true
    },
    {
      id: 'booking',
      translationKey: 'Booking',
      active: this.activeTabId() === 'booking',
      disabled: true
    },
    {
      id: 'notes',
      translationKey: 'Internal Notes',
      active: this.activeTabId() === 'notes',
      disabled: true
    },
    {
      id: 'rates',
      translationKey: 'rates.title',
      active: this.activeTabId() === 'rates',
      disabled: false,
    }
  ]);

  tabsConfig = computed<GenericTabConfig>(() => ({
    tabs: this.tabs(),
    containerClass: 'c-tabs',
    listClass: 'c-onboarding-box__tabs c-tabs__list',
    itemClass: 'c-tabs__item',
    linkClass: 'c-tabs__link',
    activeClass: 'is-active',
    disabledClass: 'disabled'
  }));

  activeTab = computed<GenericTab | null>(() =>
    this.tabs().find(tab => tab.id === this.activeTabId()) || null
  );

  activeTabIdValue = computed<string | null>(() =>
    this.activeTab()?.id || null
  );

  hasTabErrorsFunction = (tabId: string): boolean => {
    return false;
  };

  onTabChange(tab: GenericTab): void {
    this.activeTabId.set(tab.id);
  }

  onFieldValueChange(key: string, value: any): void {
    this.hasUserInteracted.set(true);
  }

  getFieldErrors(key: string): string[] {
    if (!this.showValidationErrors()) {
      return [];
    }
    return this.fieldErrors().get(key) || [];
  }

  async ngOnInit(): Promise<void> {
    this.clientId.set(this.route.snapshot.paramMap.get('id'));
    await this.initData();
  }

  private async initData(): Promise<void> {
    this.apiService.fetchData(this.endpoint()).subscribe((response) => {
      let client = new ClientModel().fromJSON(response.data);
      this.client.set(client);

      this.headerService.setHeaderConfig({
        title: client.commercialName,
        showDefaultFilter: false,
      });
    });
  }
}
